import React, { useState, FunctionComponent, useEffect } from "react";
import { Form } from "@plex/react-components";
import {
  getServiceIconBackgroundClassName,
  getServiceIconClassName,
  INodeTypeDefinition,
  NodeTypeCategory
} from "../NodeTypes/Base";
import "./NodeSelectionList.scss";
import { isMenuVisible } from "../Util/NodeUtil";
import { useViewController } from "../ViewContext";

const nodeTypeCategories = Object.values(NodeTypeCategory).filter((c) => Number(c) >= 0 === false);

const nodeAllTypeCategories = [...nodeTypeCategories];

export const NodeSelectionList: FunctionComponent = () => {
  const experimentalModeState = useViewController().experimentalModeState;

  const [filteredNodeCategories, setFilteredNodeCategories] =
    useState<(string | NodeTypeCategory)[]>(nodeAllTypeCategories);

  const [filteredNodes, setFilteredNodes] = useState<(INodeTypeDefinition | undefined)[]>(
    globalThis.nodeTypeDefinitions.getDefinitions()
  );

  const createItem = (
    nodeType: string,
    displayName: string | null = null,
    isExperimentalNode: boolean,
    index: number = 0
  ) => {
    const definition = globalThis.nodeTypeDefinitions.getDefinition(nodeType);
    if (!definition) {
      return <></>;
    }

    if (displayName === null) {
      displayName = nodeType;
    }

    return (
      <div
        key={"nodeList" + nodeType + index}
        data-testid={"nodeSelectionListItem" + nodeType}
        draggable
        className={`draggable plex-fd-nsl-node-list-item ${isExperimentalNode ? "experimental" : ""}`}
        onDragStart={(e) => {
          e.dataTransfer.setData("nodeType", nodeType);
        }}
      >
        <div className="plex-fd-nsl-node-contents-experimental-mode-off">
          <div
            key={"node-icon-" + index}
            className={"base-node-icon " + "plex-fd-nsl-node-list-item-icon " + nodeType + "-node-icon"}
          />
          <div
            key={"node-operation-icon-background-" + index}
            className={getServiceIconBackgroundClassName(definition.operationIcon)}
          />
          <div key={"node-operation-icon-" + index} className={getServiceIconClassName(definition.operationIcon)} />
          <div
            key={"node-list-item-" + index}
            className={`plex-fd-nsl-node-list-item-name ${displayName.length > 11 ? "multiline" : ""}`}
            title={displayName}
          >
            {displayName}
          </div>

          <div className="plex-fd-nsl-node-list-item-draggable-icon" />
        </div>

        {isExperimentalNode ? (
          <div key={"experiment-label-container"} className="plex-fd-nsl-experimental-label-container">
            <label key={"experiment-label"} className="plex-fd-nsl-experimental-label-text">
              {" "}
              **Experimental**{" "}
            </label>
          </div>
        ) : null}
      </div>
    );
  };

  const renderGeneralNodesSelection = (nodeComponent: INodeTypeDefinition, index: number): JSX.Element => {
    return createItem(nodeComponent.id, nodeComponent.label, nodeComponent.isExperimental, index);
  };

  const [searchText, setSearchText] = useState("");

  const searchDropdown = () => {
    // Filter nodes based on the current state of the text box
    const filteredNodes = globalThis.nodeTypeDefinitions.getDefinitions().filter((node) => {
      const labelIncludesFilter = node?.label.toUpperCase().includes(searchText.toUpperCase());
      if (!experimentalModeState) {
        return labelIncludesFilter && !node?.isExperimental;
      }
      return labelIncludesFilter;
    });

    setFilteredNodes(filteredNodes);

    const nodeCategories = filteredNodes.map((node) => node?.category) as (string | NodeTypeCategory)[];
    const mappedArraySet = new Set<number>(nodeCategories.map((index) => nodeTypeCategories[index]));
    const mappedCategories = Array.from(mappedArraySet).sort(
      (a, b) => nodeTypeCategories.indexOf(a) - nodeTypeCategories.indexOf(b)
    );

    setFilteredNodeCategories(mappedCategories);
  };

  useEffect(() => {
    searchDropdown();
  }, [experimentalModeState, searchText]);

  const displayNode = (d: any, experimentalModeState: boolean, categoryName: string | NodeTypeCategory): boolean => {
    const isCategoryMatch = (d.category as any) === NodeTypeCategory[categoryName as number];
    return isMenuVisible(d, experimentalModeState) && isCategoryMatch;
  };

  return (
    // container for the whole left panel
    <div className="plex-fd-nsl-node-list-container">
      {/** Container for the search box */}
      <div className="plex-fd-nsl-search-box-container">
        {/** Search Box */}
        <input
          type="text"
          id="plex-nodes-panel-search"
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search all nodes..."
          autoFocus={true}
          className="plex-fd-nsl-node-list-container-search-box"
        />

        <div className="plex-fd-nsl-magnifying-glass-icon" />
      </div>

      <Form className="plex-fd-nsl-form">
        {filteredNodeCategories.map((categoryName, index) => (
          <Form.Section
            key={categoryName.toString()}
            id={categoryName.toString()}
            title={categoryName.toString()}
            isCollapsed={false}
          >
            {/** This div handles the white space in the form sections */}
            <div key={"general-nodes-container"} className="plex-fd-nsl-general-nodes-container">
              {filteredNodes
                .filter((d: any) => {
                  return displayNode(d, experimentalModeState, categoryName);
                })
                .map((d: any, index) => renderGeneralNodesSelection(d, index))}
            </div>
          </Form.Section>
        ))}
      </Form>
    </div>
  );
};
